import axiosInstance from "./AxiosConfig";
import {
  getDisplayPricing,
  getProductImage,
  getProductStockQuantity,
  isProductOutOfStock,
} from "./productPresentation";
import { getBuyerToken } from "./authStorage";
import { getJsonStorageItem, isBrowser, removeStorageItem, setStorageItem } from "./browser";

export const GUEST_CART_STORAGE_KEY = "elumia_guest_cart";
export const GUEST_CART_EVENT = "elumia:guest-cart";

const normalizeQuantity = (value) => {
  const quantity = Number.parseInt(value, 10);
  return Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
};

const normalizeProductId = (item) => item?.product_id ?? item?.productId ?? item?.id;

const normalizeAttributes = (attributes) => {
  if (attributes === null || attributes === undefined || attributes === "") {
    return null;
  }

  if (typeof attributes === "object") {
    return attributes;
  }

  try {
    const parsed = JSON.parse(attributes);
    return parsed && typeof parsed === "object" ? parsed : attributes;
  } catch {
    return attributes;
  }
};

const stableSerialize = (value) => {
  if (value === null || value === undefined) return "null";
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")}]`;
  if (typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

export const getGuestCartItemKey = (item) => {
  if (item?.guest_cart_key) return String(item.guest_cart_key);

  const productId = normalizeProductId(item);
  return `${String(productId)}::${stableSerialize(normalizeAttributes(item?.attributes))}`;
};

const notifyGuestCartChange = () => {
  if (!isBrowser) return;

  window.dispatchEvent(new Event(GUEST_CART_EVENT));
  // The existing header already listens for storage events, so keep the count
  // responsive without introducing a second global state channel.
  window.dispatchEvent(new Event("storage"));
};

export const getGuestCart = () => {
  const stored = getJsonStorageItem(GUEST_CART_STORAGE_KEY, []);

  if (!Array.isArray(stored)) return [];

  return stored
    .map((item) => {
      const productId = normalizeProductId(item);
      if (productId === null || productId === undefined || productId === "") {
        return null;
      }

      return {
        product_id: productId,
        quantity: normalizeQuantity(item.quantity),
        attributes: normalizeAttributes(item.attributes),
        offer_id: item.offer_id ?? item.offerId ?? null,
      };
    })
    .filter(Boolean);
};

const saveGuestCart = (items) => {
  if (!items.length) {
    removeStorageItem(GUEST_CART_STORAGE_KEY);
  } else {
    setStorageItem(GUEST_CART_STORAGE_KEY, JSON.stringify(items));
  }

  notifyGuestCartChange();
  return items;
};

export const addGuestCartItem = (item = {}) => {
  const productId = normalizeProductId(item);
  if (productId === null || productId === undefined || productId === "") {
    return null;
  }

  const nextItem = {
    product_id: productId,
    quantity: normalizeQuantity(item.quantity),
    attributes: normalizeAttributes(item.attributes),
    offer_id: item.offer_id ?? item.offerId ?? null,
  };
  const nextKey = getGuestCartItemKey(nextItem);
  const items = getGuestCart();
  const existingIndex = items.findIndex(
    (storedItem) => getGuestCartItemKey(storedItem) === nextKey,
  );

  if (existingIndex >= 0) {
    items[existingIndex] = {
      ...items[existingIndex],
      quantity: items[existingIndex].quantity + nextItem.quantity,
      offer_id: nextItem.offer_id || items[existingIndex].offer_id || null,
    };
  } else {
    items.push(nextItem);
  }

  saveGuestCart(items);
  return nextItem;
};

export const updateGuestCartItem = (item, quantity) => {
  const itemKey = getGuestCartItemKey(item);
  const items = getGuestCart().map((storedItem) =>
    getGuestCartItemKey(storedItem) === itemKey
      ? { ...storedItem, quantity: normalizeQuantity(quantity) }
      : storedItem,
  );

  saveGuestCart(items);
  return items;
};

export const removeGuestCartItem = (item) => {
  const itemKey = getGuestCartItemKey(item);
  const items = getGuestCart().filter(
    (storedItem) => getGuestCartItemKey(storedItem) !== itemKey,
  );

  saveGuestCart(items);
  return items;
};

export const clearGuestCart = () => saveGuestCart([]);

export const getGuestCartCount = () =>
  getGuestCart().reduce((total, item) => total + normalizeQuantity(item.quantity), 0);

const getProductId = (product) => product?.id ?? product?.product_id;

const mapGuestProductToCartItem = (entry, product) => {
  const pricing = getDisplayPricing(product);
  const productId = getProductId(product);
  const availableQuantity = getProductStockQuantity(product);
  const guestCartKey = getGuestCartItemKey(entry);

  return {
    id: `guest:${guestCartKey}`,
    guest_cart_key: guestCartKey,
    product_id: productId,
    product_name: product?.name || product?.title || "Product",
    product_image: getProductImage(product),
    image_url: getProductImage(product),
    quantity: normalizeQuantity(entry.quantity),
    price: pricing.originalPrice,
    selling_price: pricing.originalPrice,
    finalPrice: pricing.finalPrice,
    discount: pricing.discountPercent,
    discountAmount: pricing.discountAmount,
    discount_amount: pricing.discountAmount,
    hasOfferPricing: Boolean(pricing.hasOffer),
    offer_id: entry.offer_id ?? pricing.offerId,
    original_price: pricing.hasOffer ? pricing.originalPrice : null,
    offer_price: pricing.hasOffer ? pricing.finalPrice : null,
    offer_discount_amount: pricing.hasOffer ? pricing.discountAmount : null,
    offer_title: pricing.offerTitle,
    offer_message: pricing.offerMessage,
    offer_end_at: product?.active_offer?.to_date ?? product?.to_date ?? null,
    is_offer_active: Boolean(pricing.hasOffer),
    totalStock: availableQuantity,
    available_quantity: availableQuantity,
    stock_quantity: availableQuantity,
    product_status: product?.status,
    approval_status: product?.approval_status,
    is_out_of_stock: isProductOutOfStock(product),
    attributes: entry.attributes,
  };
};

export const fetchGuestCartItems = async () => {
  const entries = getGuestCart();
  if (!entries.length) {
    return { items: [], missing: [] };
  }

  const productIds = Array.from(new Set(entries.map((item) => String(item.product_id))));
  const productIdBatches = [];
  for (let index = 0; index < productIds.length; index += 8) {
    productIdBatches.push(productIds.slice(index, index + 8));
  }

  const responses = await Promise.all(
    productIdBatches.map((batch) =>
      axiosInstance.get(`getProductById/${batch.join(",")}`),
    ),
  );
  const products = responses.flatMap((response) =>
    Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data)
        ? response.data
        : [],
  );
  const productById = new Map(
    products.map((product) => [String(getProductId(product)), product]),
  );
  const missing = entries.filter(
    (entry) => !productById.has(String(entry.product_id)),
  );

  if (missing.length) {
    const missingKeys = new Set(missing.map(getGuestCartItemKey));
    saveGuestCart(
      getGuestCart().filter((entry) => !missingKeys.has(getGuestCartItemKey(entry))),
    );
  }

  return {
    items: entries
      .filter((entry) => productById.has(String(entry.product_id)))
      .map((entry) => mapGuestProductToCartItem(entry, productById.get(String(entry.product_id)))),
    missing,
  };
};

let guestCartMergePromise = null;

const getMergeErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Unable to restore this item.";

const mergeGuestCartOnce = async () => {
  if (!getBuyerToken()) {
    return { mergedItems: [], failedItems: [], serverCart: null };
  }

  const items = getGuestCart();
  if (!items.length) {
    return { mergedItems: [], failedItems: [], serverCart: null };
  }

  const mergedItems = [];
  const failedItems = [];

  for (const item of items) {
    try {
      const response = await axiosInstance.post("addToCart", {
        product_id: item.product_id,
        quantity: item.quantity,
        attributes: item.attributes,
        ...(item.offer_id ? { offer_id: item.offer_id } : {}),
      });

      if (response?.data?.success === false) {
        throw new Error(response.data.message || "Unable to restore this item.");
      }

      mergedItems.push(item);
    } catch (error) {
      failedItems.push({ item, message: getMergeErrorMessage(error) });
    }
  }

  if (mergedItems.length) {
    const mergedKeys = new Set(mergedItems.map(getGuestCartItemKey));
    saveGuestCart(
      getGuestCart().filter((item) => !mergedKeys.has(getGuestCartItemKey(item))),
    );
  }

  let serverCart = null;
  let refreshError = null;
  try {
    const response = await axiosInstance.get("getCart");
    serverCart = Array.isArray(response?.data?.data) ? response.data.data : [];
  } catch (error) {
    refreshError = getMergeErrorMessage(error);
  }

  return { mergedItems, failedItems, serverCart, refreshError };
};

export const mergeGuestCartIntoUserCart = () => {
  if (!guestCartMergePromise) {
    guestCartMergePromise = mergeGuestCartOnce().finally(() => {
      guestCartMergePromise = null;
    });
  }

  return guestCartMergePromise;
};
